/**
 * Color tokens for the Speakflow Affiliates design system.
 *
 * Pulled from Speakflow's semantic CSS (_tailwind.scss, _colors.scss).
 * Primary brand: #fb2f57 (coral-red). Neutral: warm grays from Speakflow's
 * custom neutral scale. Each scale runs 50–950 for fine-grained control.
 */

export const colors = {
  // --- Brand / Primary (Speakflow coral-red #fb2f57) ---
  primary: {
    50: '#fff0f3',
    100: '#ffccd5',
    200: '#ffa3b3',
    300: '#ff7a91',
    400: '#fd5472',
    500: '#fb2f57',
    600: '#e2256b',
    700: '#C11554',
    800: '#9e1145',
    900: '#7d0e38',
    950: '#4a0621',
  },
  // --- Secondary (Speakflow brand-light #EF4591) ---
  secondary: {
    50: '#fef1f7',
    100: '#fde6f1',
    200: '#fccde4',
    300: '#faa4cd',
    400: '#f772b0',
    500: '#EF4591',
    600: '#d42a76',
    700: '#b41d5e',
    800: '#951a4e',
    900: '#7c1a44',
    950: '#4b0825',
  },
  // --- Success (green) ---
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },
  // --- Warning (amber) ---
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },
  // --- Danger (red) ---
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  // --- Neutral (from Speakflow's custom neutral scale) ---
  neutral: {
    50: '#fafafa',
    100: '#EEEEEE',
    200: '#EAEAEA',
    300: '#DDDDDD',
    400: '#CCCCCC',
    500: '#939598',
    600: '#828387',
    700: '#404040',
    800: '#292929',
    900: '#111111',
    950: '#000000',
  },
  white: '#ffffff',
  black: '#000000',
};

/**
 * Semantic color mappings — use via Tailwind classes:
 *   text-fg, text-fg-secondary, text-fg-tertiary, text-fg-inverse, text-fg-brand
 *   bg-surface, bg-surface-secondary, bg-surface-muted, bg-surface-inverse
 *   border-edge, border-edge-strong, border-edge-brand
 */
export const semanticColors = {
  fg: colors.neutral[900],
  'fg-secondary': 'rgba(17, 17, 17, 0.6)',
  'fg-tertiary': 'rgba(17, 17, 17, 0.45)',
  'fg-inverse': colors.white,
  'fg-link': colors.neutral[900],
  'fg-brand': colors.primary[500],
  'fg-success': colors.success[700],
  'fg-warning': colors.warning[700],
  'fg-danger': colors.danger[600],
  surface: colors.white,
  'surface-secondary': colors.neutral[50],
  'surface-muted': colors.neutral[100],
  'surface-inverse': colors.neutral[900],
  'surface-brand': colors.primary[500],
  'surface-brand-light': colors.primary[50],
  edge: colors.neutral[300],
  'edge-strong': colors.neutral[200],
  'edge-brand': colors.primary[500],
};
